import * as _ from 'lodash';
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { interval, Observable, of, timer } from "rxjs";
import { exhaustMap, map, retry, switchMap, take, tap } from "rxjs/operators";

import { CoreService } from "../core.service";
import { GameJackpot, GameJackpots, GameJackpotState, JackpotsCurrencyWithGames } from "./game-jackpots.model";
import { Game } from '../games/games.model';

@Injectable({
    providedIn: "root",
})
export class GameJackpotStore extends ComponentStore<GameJackpotState> {
    private readonly fetchGamesJackpots = this.coreService.jackpotsUrl;

    constructor(private http: HttpClient, private coreService: CoreService) {
        super({ jackpots: undefined });
    }

    readonly gameJackpots$: Observable<GameJackpots> = this.select(
        (state) => state.jackpots
    );
    readonly gameJackpotsTotals$: Observable<number> = this.select(
        (state) => state.totalJackpots
    );
    readonly fakedJackpots$: Observable<Game[]> = this.select(
        (state) => state.fakedJackpots
    );

    readonly addGameJackpots = this.updater((state, jackpots: GameJackpots) => ({
        ...state, jackpots: jackpots
    }));
    readonly addTotalJackpot = this.updater((state, totalJackpots: number) => ({
        ...state, totalJackpots: totalJackpots
    }));
    readonly addFakedJackpots = this.updater((state, jackpots: Game[]) => ({
        ...state, fakedJackpots: jackpots
    }));

    public getJackpots(currency: string): Observable<GameJackpots> {
        return timer(0, this.coreService.randomInterval(60000, 120000)).pipe(
            switchMap(() =>
                this.http.get<GameJackpots>(
                    this.fetchGamesJackpots + currency + ".json"
                )
            ),
            retry(3),
            tap(gameJackpots => this.addGameJackpots(gameJackpots))
        );
    }

    private getJackpotsPercentageToAnimate(total: number) {
        if (total < 10000) {
            // Thousand
            return 0.1;
        } else if (total < 100000) {
            // Hunderd thousand
            return 0.005;
        } else if (total < 1000000) {
            // Million
            return 0.0025;
        } else {
            // More then a million
            return 0.001;
        }
    }

    public getJackpotTotal = this.effect((currency$: Observable<string>) => {
        return currency$.pipe(
            switchMap(currency => this.getJackpots(currency).pipe(
                map(jackpots => jackpots.total),
                switchMap(total => {
                    // Should be 5000-10000, should be value higher than the amount of seconds we expect a jackpot total increases on the backend;
                    const totalFrames = 7891,
                        amountToAnimate = total * this.getJackpotsPercentageToAnimate(total),
                        step = amountToAnimate / totalFrames;

                    let currentAmount = total - amountToAnimate;

                    return interval(1000).pipe(
                        exhaustMap(() => {
                            currentAmount += step;
                            return of(currentAmount);
                        }),
                        take(totalFrames)
                    )
                }),
                tap((gameJackpotTotals: number) => this.addTotalJackpot(gameJackpotTotals))
            )));
    });

    /*
    *   Increments game jackpots every 1 second
    */
    public fakeJackpots = this.effect((jackpotData$: Observable<JackpotsCurrencyWithGames>) => {
        return jackpotData$.pipe(
            switchMap(jackpotData =>
                this.getJackpots(jackpotData.currency).pipe(
                    switchMap(jackpots => {
                        /*
                        *   We map data from the jackpots to the jackpot games we have in the cms
                        */
                        const jackpotsArray: GameJackpot[] = [];
                        for (let jackpot in jackpots.jackpots) {
                            jackpotsArray.push(jackpots.jackpots[jackpot])
                        }
                        let validJackpots: Game[] = _.map(
                            _.merge(
                                _.mapKeys(jackpotData.games, gameFromCms => gameFromCms.name),
                                _.mapKeys(jackpotsArray, jackpotData => jackpotData.name)
                            )
                        ).filter(value => Object.keys(value).includes('id')); // remove objects which are not found in cms games
                        // Should be 5000-10000, should be value higher than the amount of seconds we expect a jackpot total increases on the backend;
                        return interval(1000).pipe(
                            switchMap(() => {

                                validJackpots.forEach(value => {
                                    let currentAmount: number = value.value + Math.random() * (2 - 0.02) + 0.02;
                                    value.value = currentAmount;
                                });
                                return of(_.cloneDeep(validJackpots));
                            }),
                            tap((newJackpots) => this.addFakedJackpots(newJackpots))
                        )
                    }),
                )));
    })
};
